/**
 * Authenticated fetch wrapper with XMLHttpRequest fallback.
 * 
 * 1. Reads session token from localStorage, sends as Bearer token
 * 2. Keeps credentials: "include" as cookie fallback
 * 3. If fetch() is blocked by security software, falls back to XMLHttpRequest
 */
export function authFetch(url, options = {}) {
  const stored = localStorage.getItem('cfi_user');
  let token = null;
  try {
    token = stored ? JSON.parse(stored)?.session_token : null;
  } catch {}

  const headers = new Headers(options.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include',
  };

  return fetch(url, fetchOptions).catch((fetchError) => {
    // If fetch was blocked by security software/proxy, try XMLHttpRequest
    const msg = fetchError?.message || '';
    if (
      msg.includes('Failed to fetch') ||
      msg.includes('NetworkError') ||
      msg.includes('Body is disturbed') ||
      msg.includes('postMessage') ||
      msg.includes('clone') ||
      msg.includes('blocked') ||
      msg.includes('abort')
    ) {
      console.warn('fetch() blocked, falling back to XMLHttpRequest for:', url);
      return xhrFallback(url, fetchOptions, token);
    }
    throw fetchError;
  });
}

function xhrFallback(url, options, token) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    xhr.withCredentials = true;
    xhr.timeout = 30000;

    // Copy headers
    if (options.headers) {
      const h = options.headers instanceof Headers ? options.headers : new Headers(options.headers);
      h.forEach((value, key) => {
        xhr.setRequestHeader(key, value);
      });
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = () => {
      // Build a Response-like object
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        json: () => {
          try {
            return Promise.resolve(JSON.parse(xhr.responseText));
          } catch {
            return Promise.reject(new Error('Invalid JSON'));
          }
        },
        text: () => Promise.resolve(xhr.responseText),
        headers: {
          get: (name) => xhr.getResponseHeader(name),
        },
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Request timed out'));

    // Send body
    if (options.body) {
      xhr.send(options.body instanceof FormData ? options.body : options.body);
    } else {
      xhr.send();
    }
  });
}
